import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../utils/cn';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

/**
 * Visual chrome shared by the Tooltip primitive and the graph hover overlay
 * (`GraphHoverTooltip`) so every hover surface renders an identical card.
 * Layout-only utilities (whitespace handling, positioning) are applied by the
 * consumer — this constant covers surface, border, typography and entrance only.
 */
export const TOOLTIP_SURFACE_CLASS = cn(
	'bg-overlay backdrop-blur-[12px] px-2 py-1 rounded-lg',
	'text-[12px] font-medium text-neutral',
	'shadow-md border border-border-weak',
	'animate-in fade-in-0 zoom-in-95 duration-150'
);

interface TooltipProps {
	children: React.ReactNode;
	content: React.ReactNode;
	position?: TooltipPosition;
	delay?: number;
	offset?: number;
	className?: string;
	triggerClassName?: string;
	disabled?: boolean;
	/**
	 * Controlled show: keeps the tooltip open regardless of hover/focus (no
	 * delay). Hover behavior is unaffected — the tooltip hides once BOTH this
	 * is false and the pointer has left.
	 */
	forceVisible?: boolean;
}

interface PositionStyle {
	top: number;
	left: number;
	transformOrigin: string;
}

const calculatePosition = (
	triggerRect: DOMRect,
	tooltipRect: DOMRect,
	position: TooltipPosition,
	offset: number
): PositionStyle => {
	let top = 0;
	let left = 0;
	let transformOrigin = 'center';

	switch (position) {
		case 'top':
			top = triggerRect.top - tooltipRect.height - offset;
			left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
			transformOrigin = 'bottom center';
			break;
		case 'bottom':
			top = triggerRect.bottom + offset;
			left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
			transformOrigin = 'top center';
			break;
		case 'left':
			top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
			left = triggerRect.left - tooltipRect.width - offset;
			transformOrigin = 'center right';
			break;
		case 'right':
			top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
			left = triggerRect.right + offset;
			transformOrigin = 'center left';
			break;
	}

	// Viewport boundary checks
	const padding = 8;
	const viewportWidth = window.innerWidth;
	const viewportHeight = window.innerHeight;

	// Clamp to viewport
	if (left < padding) left = padding;
	if (left + tooltipRect.width > viewportWidth - padding) {
		left = viewportWidth - tooltipRect.width - padding;
	}
	if (top < padding) top = padding;
	if (top + tooltipRect.height > viewportHeight - padding) {
		top = viewportHeight - tooltipRect.height - padding;
	}

	return { top, left, transformOrigin };
};

export const Tooltip = ({
	children,
	content,
	position = 'top',
	delay = 300,
	offset = 4,
	className = '',
	triggerClassName,
	disabled = false,
	forceVisible = false
}: TooltipProps) => {
	const [isVisible, setIsVisible] = useState(false);
	const [positionStyle, setPositionStyle] = useState<PositionStyle>({
		top: 0,
		left: 0,
		transformOrigin: 'center'
	});
	const triggerRef = useRef<HTMLSpanElement>(null);
	const tooltipRef = useRef<HTMLDivElement>(null);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	// Where the pointer entered the trigger. Null = not hovering, or hover
	// already armed. See `handleMouseMove` for why enter alone never opens.
	const enterPointRef = useRef<{ x: number; y: number } | null>(null);

	const updatePosition = useCallback(() => {
		if (!triggerRef.current || !tooltipRef.current) return;

		const triggerRect = triggerRef.current.getBoundingClientRect();
		const tooltipRect = tooltipRef.current.getBoundingClientRect();

		const newPosition = calculatePosition(triggerRect, tooltipRect, position, offset);
		setPositionStyle(newPosition);
	}, [position, offset]);

	const showTooltip = useCallback(() => {
		if (disabled) return;

		// One pending show at a time. Hover intent and focus both arm this (a
		// click on a hovered trigger focuses it), and overwriting the ref used to
		// orphan the first timer: `hideTooltip` cleared only the second, so the
		// orphan fired after the pointer had left and left the tooltip stuck.
		if (timeoutRef.current) clearTimeout(timeoutRef.current);
		timeoutRef.current = setTimeout(() => {
			timeoutRef.current = null;
			setIsVisible(true);
		}, delay);
	}, [delay, disabled]);

	const hideTooltip = useCallback(() => {
		enterPointRef.current = null;
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
		setIsVisible(false);
	}, []);

	const handleMouseEnter = useCallback((e: React.MouseEvent) => {
		enterPointRef.current = { x: e.clientX, y: e.clientY };
	}, []);

	// Hover intent: arm only once the pointer has actually moved over the
	// trigger. Browsers also fire mouseenter when a trigger slides in under a
	// resting cursor (the composer expanding its toolbar under the click
	// point), and that must not pop a tooltip over the text being typed.
	const handleMouseMove = useCallback((e: React.MouseEvent) => {
		const enter = enterPointRef.current;
		if (!enter || (e.clientX === enter.x && e.clientY === enter.y)) return;
		enterPointRef.current = null;
		showTooltip();
	}, [showTooltip]);

	const visible = isVisible || forceVisible;

	// Update position when visible
	useEffect(() => {
		if (!visible) return;

		// Initial position update with RAF to ensure tooltip is rendered
		const rafId = requestAnimationFrame(updatePosition);

		// Re-anchor when the content resizes after mounting (e.g. a node preview
		// whose description loads lazily), so it stays clamped to the viewport.
		const el = tooltipRef.current;
		const observer = el && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => updatePosition()) : null;
		if (el) observer?.observe(el);

		return () => {
			cancelAnimationFrame(rafId);
			observer?.disconnect();
		};
	}, [visible, updatePosition]);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	return (
		<>
			<span
				ref={triggerRef}
				onMouseEnter={handleMouseEnter}
				onMouseMove={handleMouseMove}
				onMouseLeave={hideTooltip}
				onFocus={showTooltip}
				onBlur={hideTooltip}
				className={cn("inline-flex", triggerClassName)}
			>
				{children}
			</span>

			{typeof document !== 'undefined' && visible && createPortal(
				<div
					ref={tooltipRef}
					role="tooltip"
					className="fixed z-[9999] pointer-events-none max-md:hidden"
					style={{
						top: positionStyle.top,
						left: positionStyle.left
					}}
				>
					<div
						className={cn(TOOLTIP_SURFACE_CLASS, 'whitespace-nowrap', className)}
						style={{ transformOrigin: positionStyle.transformOrigin }}
					>
						{content}
					</div>
				</div>,
				document.body
			)}
		</>
	);
};
